import type { IconName } from './glyphs';
import {
    FA_640_VIEWBOX,
    evenOddIcons,
    fa640ScaledIcons,
    pathFillOpacities,
    pathsFor,
} from './glyphs';

export type BuildEmailIconSvgOptions = {
    size?: number;
    /** Default matches newsletter body / heading tone */
    fill?: string;
};

function escapeXmlAttr(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/**
 * Inline SVG string for HTML email — same paths / transforms as the shared {@link Icon} component.
 */
export function buildEmailIconSvg(name: IconName, opts: BuildEmailIconSvgOptions = {}): string {
    const size = opts.size ?? 42;
    const fill = opts.fill ?? '#213130';
    const fillEsc = escapeXmlAttr(fill);
    const scale = 24 / FA_640_VIEWBOX;
    const scaleAttr =
        fa640ScaledIcons.has(name) ? ` transform="scale(${scale.toFixed(6)})"` : '';

    const paths = pathsFor(name).map((d, i) => {
        const fo = pathFillOpacities[name]?.[i];
        const foAttr = fo != null ? ` fill-opacity="${fo}"` : '';
        const ruleAttr = evenOddIcons.has(name) ? ' fill-rule="evenodd" clip-rule="evenodd"' : '';
        return `<path d="${escapeXmlAttr(d)}" fill="${fillEsc}"${foAttr}${ruleAttr}${scaleAttr}/>`;
    });

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" role="img" aria-hidden="true">${paths.join('')}</svg>`;
}

/**
 * Data-URI {@code <img>} for email. Prefer **base64** (many clients block or mishandle URL-encoded SVG).
 */
export function buildEmailIconDataImg(
    name: IconName,
    opts: BuildEmailIconSvgOptions & { alt?: string } = {},
): string {
    const size = opts.size ?? 24;
    const svg = buildEmailIconSvg(name, opts);
    const compact = svg.replace(/\s+/g, ' ').trim();
    const b64 = Buffer.from(compact, 'utf8').toString('base64');
    const altEsc = opts.alt != null ? escapeXmlAttr(opts.alt) : '';
    return `<img src="data:image/svg+xml;base64,${b64}" width="${size}" height="${size}" alt="${altEsc}" style="display:block;border:0;outline:none;text-decoration:none;width:${size}px;height:${size}px;max-width:${size}px;line-height:0" />`;
}
